import { useState, useEffect, type RefObject } from 'react';
import { throttle } from 'lodash-es';

/**
 * 사용자가 특정 스크롤 임계값을 초과했는지 여부를 추적하는 커스텀 훅.
 *
 * @param targetRef - 스크롤을 감지할 대상 요소의 ref. 없으면 window 스크롤 감지.
 * @param threshold - 스크롤 위치(픽셀 단위) 임계값. 초과 시 상태 변경 (기본값: 0).
 * @param throttleDelay - 스크롤 이벤트 핸들러에 적용할 쓰로틀링 딜레이(ms). (기본값: 100).
 * @returns 스크롤 위치가 임계값을 초과하면 `true`, 그렇지 않으면 `false`.
 */
export const useScrollState = ({
  targetRef,
  threshold = 0,
  throttleDelay = 100,
}: { targetRef?: RefObject<HTMLElement>; threshold?: number; throttleDelay?: number } = {}) => {
  // 스크롤이 임계값을 초과했는지 여부를 저장하는 상태
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollPosition = targetRef?.current?.scrollTop ?? window.scrollY;
      setHasScrolled(scrollPosition > threshold);
    }, throttleDelay);

    const target = targetRef?.current;

    if (target) {
      // targetRef가 있으면 해당 요소의 스크롤 감지
      target.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll(); // 초기 상태 체크

      return () => {
        target.removeEventListener('scroll', handleScroll);
        handleScroll.cancel();
      };
    }
    // targetRef가 없으면 window 스크롤 감지
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // 초기 상태 체크

    return () => {
      window.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [targetRef, threshold, throttleDelay]);

  return hasScrolled;
};
